import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner, Row, Col, Alert } from "react-bootstrap";
import axios from "axios";
import { emptyTier, hasTierErrors, validateTiers } from "../utils/pricingTierUtil";

function EditRegistrationModal({ show, handleClose, season, onSeasonUpdated }) {
    const [formData, setFormData] = useState({
        registration_open: false,
        registration_start_date: "",
        registration_deadline: "",
        max_teams: "",
    });

    const [tiers, setTiers] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);

    // Re-sync the form with the current season whenever the modal opens.
    useEffect(() => {
        if (show && season) {
            setFormData({
                registration_open: !!season.registration_open,
                registration_start_date: season.registration_start_date || "",
                registration_deadline: season.registration_deadline || "",
                max_teams: season.max_teams ?? "",
            });
            // Carry each tier's id so the server updates the existing row
            // instead of replacing it.
            setTiers(
                (season.pricing_tiers || []).map((tier) => ({
                    id: tier.id,
                    name: tier.name || "",
                    start_date: tier.start_date || "",
                    end_date: tier.end_date || "",
                    fee: tier.fee ?? "",
                }))
            );
            setFieldErrors({});
            setErrorMessages([]);
        }
    }, [show, season]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const addTier = () => setTiers((prev) => [...prev, emptyTier(prev.length)]);

    const removeTier = (idx) => setTiers((prev) => prev.filter((_, i) => i !== idx));

    const updateTier = (idx, field, value) =>
        setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));

    const tierErrors = fieldErrors.pricing_tiers || [];
    const errorText = (value) => (Array.isArray(value) ? value.join(" ") : value);

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrorMessages([]);
        setFieldErrors({});

        if (tiers.length > 0) {
            const missingWindow = {};
            if (!formData.registration_start_date) {
                missingWindow.registration_start_date = ["Required when pricing tiers are defined."];
            }
            if (!formData.registration_deadline) {
                missingWindow.registration_deadline = ["Required when pricing tiers are defined."];
            }

            const rowErrors = validateTiers(
                tiers,
                formData.registration_start_date,
                formData.registration_deadline
            );
            if (Object.keys(missingWindow).length || hasTierErrors(rowErrors)) {
                setFieldErrors({ ...missingWindow, pricing_tiers: rowErrors });
                return;
            }
        }

        setLoading(true);

        const payload = {
            registration_open: formData.registration_open,
            registration_start_date: formData.registration_start_date || null,
            registration_deadline: formData.registration_deadline || null,
            max_teams: formData.max_teams === "" ? null : Number(formData.max_teams),
            // Always sent: an empty array is how the server is told to clear
            // every remaining tier.
            pricing_tiers: tiers,
        };

        axios
            .patch(`/api/leagues/seasons/${season.id}/settings/`, payload, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            })
            .then((response) => {
                onSeasonUpdated(response.data);
                handleClose();
            })
            .catch((error) => {
                console.error("Error updating registration settings:", error.response?.data);
                const data = error.response?.data || {};
                if (data && typeof data === "object") {
                    setFieldErrors(data);
                    const generic = Object.entries(data)
                        .filter(([field]) => field !== "pricing_tiers")
                        .flatMap(([field, msgs]) =>
                            (Array.isArray(msgs) ? msgs : [msgs]).map(
                                (m) => `${field !== "non_field_errors" ? `[${field}] ` : ""}${m}`
                            )
                        );
                    setErrorMessages(
                        generic.length ? generic : ["Please correct the highlighted fields."]
                    );
                } else {
                    setErrorMessages(["Failed to update registration settings."]);
                }
            })
            .finally(() => setLoading(false));
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Registration Settings</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMessages.length > 0 && (
                    <Alert variant="danger">
                        {errorMessages.map((err, i) => (
                            <div key={i}>{err}</div>
                        ))}
                    </Alert>
                )}
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="switch"
                            id="registration-open-switch"
                            name="registration_open"
                            label="Registration Open"
                            checked={formData.registration_open}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group controlId="registration_start_date">
                                <Form.Label>Registration Start Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="registration_start_date"
                                    value={formData.registration_start_date}
                                    onChange={handleChange}
                                    isInvalid={!!fieldErrors.registration_start_date}
                                />
                                {fieldErrors.registration_start_date && (
                                    <div className="invalid-feedback d-block small">
                                        {errorText(fieldErrors.registration_start_date)}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="registration_deadline">
                                <Form.Label>Registration Deadline</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="registration_deadline"
                                    value={formData.registration_deadline}
                                    onChange={handleChange}
                                    isInvalid={!!fieldErrors.registration_deadline}
                                />
                                {fieldErrors.registration_deadline && (
                                    <div className="invalid-feedback d-block small">
                                        {errorText(fieldErrors.registration_deadline)}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group controlId="max_teams">
                                <Form.Label>Max Teams</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    name="max_teams"
                                    value={formData.max_teams}
                                    onChange={handleChange}
                                    placeholder="No limit"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Pricing Tiers */}
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="mb-0">Pricing Tiers</Form.Label>
                            <button
                                type="button"
                                className="btn btn-link text-secondary p-0 rounded-circle"
                                onClick={addTier}
                                title="Add pricing tier"
                            >
                                <i className="bi bi-plus-circle fs-5"></i>
                            </button>
                        </div>

                        {tiers.length === 0 ? (
                            <div className="text-muted small">
                                No pricing tiers. Registration will have no fee until one is added.
                            </div>
                        ) : (
                            tiers.map((tier, idx) => {
                                const rowError = tierErrors[idx] || {};
                                return (
                                    <div className="row g-2 align-items-start mb-2" key={tier.id ?? `new-${idx}`}>
                                        <div className="col-12 col-md-3">
                                            {idx === 0 && <label className="form-label small mb-1">Name</label>}
                                            <input
                                                type="text"
                                                className={`form-control form-control-sm ${rowError.name ? "is-invalid" : ""}`}
                                                value={tier.name}
                                                onChange={(e) => updateTier(idx, "name", e.target.value)}
                                                placeholder="Name"
                                            />
                                            {rowError.name && (
                                                <div className="invalid-feedback d-block small">
                                                    {errorText(rowError.name)}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-6 col-md-3">
                                            {idx === 0 && <label className="form-label small mb-1">Start</label>}
                                            <input
                                                type="date"
                                                className={`form-control form-control-sm ${rowError.start_date ? "is-invalid" : ""}`}
                                                value={tier.start_date}
                                                onChange={(e) => updateTier(idx, "start_date", e.target.value)}
                                            />
                                            {rowError.start_date && (
                                                <div className="invalid-feedback d-block small">
                                                    {errorText(rowError.start_date)}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-6 col-md-3">
                                            {idx === 0 && <label className="form-label small mb-1">End</label>}
                                            <input
                                                type="date"
                                                className={`form-control form-control-sm ${rowError.end_date ? "is-invalid" : ""}`}
                                                value={tier.end_date}
                                                onChange={(e) => updateTier(idx, "end_date", e.target.value)}
                                            />
                                            {rowError.end_date && (
                                                <div className="invalid-feedback d-block small">
                                                    {errorText(rowError.end_date)}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-8 col-md-2">
                                            {idx === 0 && <label className="form-label small mb-1">Fee</label>}
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text">$</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    className={`form-control ${rowError.fee ? "is-invalid" : ""}`}
                                                    value={tier.fee}
                                                    onChange={(e) => updateTier(idx, "fee", e.target.value)}
                                                />
                                            </div>
                                            {rowError.fee && (
                                                <div className="invalid-feedback d-block small">
                                                    {errorText(rowError.fee)}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-4 col-md-1">
                                            {idx === 0 && <label className="form-label small mb-1 d-block">&nbsp;</label>}
                                            <button
                                                type="button"
                                                className="btn btn-link text-danger p-0"
                                                onClick={() => removeTier(idx)}
                                                title="Remove tier"
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <div className="text-end">
                        <Button variant="secondary" onClick={handleClose} className="me-2">
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : "Save Changes"}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}

export default EditRegistrationModal;
